import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useFundraiserProgress } from '@/hooks/useFundraiserProgress';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { Fundraiser } from '@/types/fundraiser';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { SupportersLeaderboard } from '@/components/SupportersLeaderboard';
import { QuickZapButton } from '@/components/QuickZapButton';
import { AnonymousZapButton } from '@/components/AnonymousZapButton';
import { Calendar, Target, Zap, ExternalLink, Share2, Users, Rocket } from 'lucide-react';
import { genUserName } from '@/lib/genUserName';
import { useToast } from '@/hooks/useToast';
import confetti from 'canvas-confetti';

interface FundraiserDetailProps {
  fundraiser?: Fundraiser;
}

export default function FundraiserDetail({ fundraiser: propFundraiser }: FundraiserDetailProps) {
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const [zapAnimation, setZapAnimation] = useState(false);

  const fundraiser = propFundraiser;
  const fundraiserLoading = false;
  const { data: progress, isLoading: progressLoading, refetch: refetchProgress } = useFundraiserProgress(fundraiser);
  const { data: author } = useAuthor(fundraiser?.pubkey || '');

  const metadata = author?.metadata;
  const displayName = metadata?.name || genUserName(fundraiser?.pubkey || '');
  const avatarUrl = metadata?.picture;

  const shareUrl = typeof window !== 'undefined' && fundraiser 
    ? `${window.location.origin}?fund=${fundraiser.event.id}` 
    : '';

  useSeoMeta({
    title: fundraiser ? `${fundraiser.title} - ZapFunder` : 'Campaign - ZapFunder',
    description: fundraiser?.summary || fundraiser?.content.slice(0, 160) || 'Support this campaign on Nostr with Bitcoin Lightning',
    ogTitle: fundraiser?.title || 'Campaign - ZapFunder',
    ogDescription: fundraiser?.summary || fundraiser?.content.slice(0, 160) || 'Support this campaign on Nostr with Bitcoin Lightning',
    ogImage: fundraiser?.image,
    ogType: 'website',
    ogUrl: shareUrl,
    ogSiteName: 'ZapFunder',
    twitterCard: 'summary_large_image',
    twitterTitle: fundraiser?.title || 'Campaign - ZapFunder',
    twitterDescription: fundraiser?.summary || fundraiser?.content.slice(0, 160) || 'Support this campaign on Nostr',
    twitterImage: fundraiser?.image,
  });

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: fundraiser?.title,
          text: fundraiser?.summary,
          url: shareUrl,
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast({
          title: 'Link copied!',
          description: 'Share link copied to clipboard',
        });
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    }
  };

  const handleZapSuccess = () => {
    setZapAnimation(true);
    
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { 
      startVelocity: 30, 
      spread: 360, 
      ticks: 60, 
      zIndex: 100,
      colors: ['#FF6B00', '#FF8533', '#FFA366', '#FFCC00', '#FFD700']
    };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);
      
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    setTimeout(() => setZapAnimation(false), 3000);
    
    setTimeout(() => {
      refetchProgress();
    }, 1500);
  };

  if (!fundraiser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-6 lg:space-y-8">
              <Skeleton className="h-96 w-full rounded-3xl" />
              <Skeleton className="h-60 w-full rounded-3xl" />
            </div>
            <div>
              <Skeleton className="h-[600px] w-full rounded-3xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!fundraiser) {
    return null;
  }

  const timeRemaining = fundraiser.closedAt
    ? Math.max(0, fundraiser.closedAt - Math.floor(Date.now() / 1000))
    : null;

  const daysRemaining = timeRemaining ? Math.ceil(timeRemaining / 86400) : null;
  const percentComplete = progress?.percentComplete || 0;
  const goalInSats = Math.floor(fundraiser.goal / 1000);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <Header />

      {/* Epic Zap Success Animation */}
      <AnimatePresence>
        {zapAnimation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center bg-gradient-to-br from-orange-500/20 via-transparent to-orange-500/20 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180, y: 100 }}
              animate={{ 
                scale: [0, 2, 1.5],
                rotate: [0, 720, 720],
                y: [100, -30, 0]
              }}
              exit={{ scale: 0, opacity: 0, y: -100 }}
              transition={{ 
                duration: 1.2, 
                ease: [0.34, 1.56, 0.64, 1]
              }}
              className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-full p-16 shadow-2xl"
            >
              <Rocket className="h-48 w-48 text-white drop-shadow-2xl" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-32"
            >
              <div className="bg-white px-8 py-4 rounded-full shadow-2xl border-4 border-orange-500">
                <p className="text-3xl font-black text-orange-600">Thank You! 🎉</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="lg:col-span-2 space-y-6 lg:space-y-8"
          >
            {/* Hero Image */}
            {fundraiser.image && (
              <div className="relative w-full h-[300px] sm:h-[400px] md:h-[450px] rounded-2xl sm:rounded-3xl overflow-hidden border-2 border-orange-500/20 shadow-2xl">
                <img
                  src={fundraiser.image}
                  alt={fundraiser.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                
                {progress?.isComplete && (
                  <Badge className="absolute top-3 sm:top-4 md:top-6 right-3 sm:right-4 md:right-6 bg-green-500 text-white border-0 text-xs sm:text-sm md:text-lg px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-3 shadow-2xl font-black">
                    <Target className="h-3 sm:h-4 md:h-5 w-3 sm:w-4 md:w-5 mr-1 sm:mr-1.5 md:mr-2" />
                    Fully Funded
                  </Badge>
                )}
                {progress?.isClosed && !progress?.isComplete && (
                  <Badge className="absolute top-3 sm:top-4 md:top-6 right-3 sm:right-4 md:right-6 bg-orange-500 text-white border-0 text-xs sm:text-sm md:text-lg px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-3 shadow-2xl font-black">
                    Campaign Closed
                  </Badge>
                )}
              </div>
            )}

            {/* Title & Meta */}
            <div>
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <Avatar className="h-10 sm:h-12 md:h-16 w-10 sm:w-12 md:w-16 border-2 sm:border-3 border-orange-500 ring-2 sm:ring-4 ring-orange-500/20">
                  <AvatarImage src={avatarUrl} alt={displayName} />
                  <AvatarFallback className="bg-orange-500 text-white text-sm sm:text-lg md:text-xl font-bold">
                    {displayName.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-semibold text-orange-600">Campaign by</p>
                  <p className="text-sm sm:text-base md:text-xl font-black text-black truncate">{displayName}</p>
                </div>
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black text-black mb-3 sm:mb-4 md:mb-6 leading-tight tracking-tight break-words">
                {fundraiser.title}
              </h1>

              {fundraiser.summary && (
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-semibold break-words">
                  {fundraiser.summary}
                </p>
              )}
            </div>

            {/* Description */}
            <Card className="border-2 border-orange-500/20 bg-white shadow-xl">
              <CardContent className="p-4 sm:p-6 md:p-8 lg:p-10">
                <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-black mb-3 sm:mb-4 md:mb-6">About This Campaign</h2>
                <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none">
                  <p className="text-sm sm:text-base md:text-lg text-gray-800 leading-relaxed whitespace-pre-wrap font-medium break-words">
                    {fundraiser.content}
                  </p>
                </div>

                {fundraiser.references.length > 0 && (
                  <div className="mt-4 sm:mt-6 md:mt-8 space-y-2">
                    {fundraiser.references.map((url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 transition-colors font-bold text-sm sm:text-base md:text-lg break-all"
                      >
                        <ExternalLink className="h-3 sm:h-4 md:h-5 w-3 sm:w-4 md:w-5 flex-shrink-0" />
                        <span className="break-all">{url}</span>
                      </a>
                    ))}
                  </div>
                )}

                {fundraiser.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4 sm:mt-6 md:mt-8 pt-4 sm:pt-6 md:pt-8 border-t-2 border-orange-500/20">
                    {fundraiser.hashtags.map(tag => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 text-xs sm:text-sm font-bold"
                      >
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Leaderboard */}
            {progress && progress.supporters && progress.supporters.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <SupportersLeaderboard supporters={progress.supporters} />
              </motion.div>
            )}
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
            className="lg:sticky lg:top-24 lg:self-start space-y-4 lg:space-y-6"
          >
            <Card className="border-2 border-orange-500/20 bg-white shadow-2xl">
              <CardContent className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 md:space-y-8">
                {/* Main Stats */}
                <div className="space-y-3 sm:space-y-4 md:space-y-6">
                  <div>
                    <div className="flex items-baseline justify-between mb-2 sm:mb-3 md:mb-4">
                      <span className="text-3xl sm:text-4xl md:text-5xl font-black text-black">
                        {progressLoading ? '...' : (progress?.totalRaised || 0).toLocaleString()}
                      </span>
                      <span className="text-lg sm:text-xl md:text-2xl font-black text-orange-600">
                        {progressLoading ? '...' : `${Math.round(percentComplete)}%`}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm md:text-base font-bold text-gray-700 mb-3 sm:mb-4 md:mb-5">
                      sats raised of {goalInSats.toLocaleString()} goal
                    </p>
                    
                    {/* Enhanced Progress Bar */}
                    <div className="relative h-4 sm:h-5 md:h-6 bg-orange-100 rounded-full overflow-hidden border-2 border-orange-500/30">
                      <motion.div
                        animate={{ width: `${percentComplete}%` }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" 
                             style={{ backgroundSize: '200% 100%' }} />
                      </motion.div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 pt-3 sm:pt-4 md:pt-6 border-t-2 border-orange-500/20">
                    <div>
                      <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 text-orange-600 mb-1 sm:mb-1.5 md:mb-2">
                        <Users className="h-3 sm:h-4 md:h-5 w-3 sm:w-4 md:w-5" />
                        <span className="text-xs sm:text-sm font-bold">Supporters</span>
                      </div>
                      <p className="text-xl sm:text-2xl md:text-3xl font-black text-black">
                        {progressLoading ? '...' : progress?.zapCount || 0}
                      </p>
                    </div>

                    {daysRemaining !== null && !progress?.isClosed && (
                      <div>
                        <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 text-orange-600 mb-1 sm:mb-1.5 md:mb-2">
                          <Calendar className="h-3 sm:h-4 md:h-5 w-3 sm:w-4 md:w-5" />
                          <span className="text-xs sm:text-sm font-bold">Days Left</span>
                        </div>
                        <p className="text-xl sm:text-2xl md:text-3xl font-black text-black">
                          {daysRemaining === 0 ? 'Today' : daysRemaining}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3 pt-3 sm:pt-4 md:pt-6 border-t-2 border-orange-500/20">
                  {!progress?.isClosed && user && (
                    <QuickZapButton
                      target={fundraiser.event}
                      onZapSuccess={handleZapSuccess}
                      className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white h-12 sm:h-14 md:h-16 text-base sm:text-lg md:text-xl font-black shadow-xl shadow-orange-500/30 hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300"
                    />
                  )}

                  {!progress?.isClosed && !user && (
                    <AnonymousZapButton
                      target={fundraiser.event}
                      onInvoiceGenerated={handleZapSuccess}
                      onPaymentSuccess={() => {
                        handleZapSuccess();
                        // Refetch progress after payment
                        setTimeout(() => refetchProgress(), 3000);
                      }}
                    />
                  )}

                  <Button
                    variant="outline"
                    className="w-full border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-bold h-10 sm:h-12 md:h-14 text-sm sm:text-base md:text-lg"
                    onClick={handleShare}
                  >
                    <Share2 className="mr-2 h-3 sm:h-4 md:h-5 w-3 sm:w-4 md:w-5" />
                    Share Campaign
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
